<template>
  <div>
    <!-- HEADER avec bouton dynamique -->
    <v-layout row wrap text-xs-center>
      <v-flex xs12>
        <h2 class="font-weight-bold text-xs-left">
          <u v-if="loan.is_legacy">Loan: {{ loan.name }}</u>
          <u v-else>Loan #{{ loan.id }} - {{ formatAmount(loan.loan_amount) }} FCFA</u>
        </h2>
      </v-flex>

      <!-- Bouton d'action selon le type -->
      <v-flex xs12 class="text-xs-right" v-if="showActionButton">
        <v-btn
          small
          class="animate__animated animate__flash animate__delay-1s elevation-6 white--text ma-0 text-xs-center"
          round
          :color="loan.is_legacy ? 'teal darken-2' : 'primary'"
          :to="actionRoute"
        >
          <v-icon left small>{{ actionIcon }}</v-icon>
          {{ actionLabel }}
        </v-btn>
      </v-flex>
    </v-layout>

    <!-- 🟦 AFFICHAGE LEGACY -->
    <template v-if="loan.is_legacy">
      <v-card class="primary darken-2 mt-1">
        <v-card-text class="pa-2" style="font-size: 12px">
          <span class="white--text text-xs-left subheading">
            <b>Amount Saved:</b>
            <span class="pa-0 ma-1 dark--text text-xs-left body-2">
              {{ loan.saved_amount || 0 }} FCFA
              <span v-if="loan.passed_days">in {{ loan.passed_days }} days</span>
            </span>
          </span>
        </v-card-text>
      </v-card>

      <v-card class="white elevation-6">
        <v-card-text class="body-2 pa-2" style="font-size: 12px">
          <div><b>Creation Date:</b> <span class="caption">{{ loan.get_date_str }}</span></div>
          <div v-if="loan.target_amount && loan.target_date">
            <b>Objective:</b> <span class="caption">Reach {{ loan.target_amount }} FCFA by {{ loan.target_date }}</span>
          </div>
          <div v-else-if="loan.target_amount">
            <b>Objective:</b> <span class="caption">Reach {{ loan.target_amount }} FCFA</span>
          </div>
          <div v-else-if="loan.target_date">
            <b>Objective:</b> <span class="caption">On {{ loan.target_date }}</span>
            <v-spacer></v-spacer>
            <b>Remaining:</b> <span class="caption">{{ loan.remaining_days }} days</span>
          </div>
          <v-spacer></v-spacer>
          <div><b>Percentage Complete:</b> <span class="caption">{{ loan.percentage_completed || '0%' }}</span></div>
          <div><b>Description:</b> <span class="caption">{{ loan.description }}</span></div>
          <div>
            <b>State:</b>
            <span v-if="loan.cancelled" class="caption red--text">Cancelled</span>
            <span v-else-if="loan.validated" class="caption green--text">Validated</span>
            <span v-else class="caption primary--text">Inactive</span>
          </div>
        </v-card-text>
      </v-card>
    </template>

    <!-- 🟩 AFFICHAGE NOUVEAU PRÊT -->
    <template v-else>
      <v-card class="primary darken-2 mt-1">
        <v-card-text class="pa-2" style="font-size: 12px">
          <span class="white--text text-xs-left subheading">
            <b>Amount Repaid (confirmed):</b>
            <span class="pa-0 ma-1 dark--text text-xs-left body-2">
              {{ formatAmount(loan.amount_paid_confirmed) }} / {{ formatAmount(loan.total_amount) }} FCFA
            </span>
          </span>
        </v-card-text>
      </v-card>

      <!-- Afficher les paiements en attente s'il y en a (résumé) -->
      <v-card v-if="parseFloat(loan.pending_payments) > 0" class="warning lighten-5 mt-1">
        <v-card-text class="pa-2" style="font-size: 12px">
          <span class="text-xs-left">
            <b>Pending repayments:</b> {{ formatAmount(loan.pending_payments) }} FCFA
            <span class="caption">(awaiting confirmation)</span>
          </span>
        </v-card-text>
      </v-card>

      <v-card class="white elevation-6">
        <v-card-text class="body-2 pa-2" style="font-size: 12px">
          <v-layout row wrap>
            <v-flex xs6><b>Principal:</b> {{ formatAmount(loan.loan_amount) }} FCFA</v-flex>
            <v-flex xs6><b>Total Due:</b> {{ formatAmount(loan.total_amount) }} FCFA</v-flex>
            <v-flex xs6><b>Remaining:</b> <span class="orange--text">{{ formatAmount(loan.remaining_amount) }} FCFA</span></v-flex>
            <v-flex xs12><b>Due Date:</b> {{ loan.due_date || '—' }}</v-flex>
            <v-flex xs12><b>Purpose:</b> {{ loan.purpose || 'Not specified' }}</v-flex>
            <v-flex xs12><b>Status:</b> <v-chip x-small :color="statusColor">{{ loan.status }}</v-chip></v-flex>
            <v-flex xs12><b>Progress:</b> {{ loan.progress_percent }}%</v-flex>
          </v-layout>
          <v-progress-linear :value="loan.progress_percent" color="primary" height="6" rounded class="mt-2"></v-progress-linear>
        </v-card-text>
      </v-card>
    </template>

    <!-- ========== SECTION : TRANSACTIONS DEMANDÉES (EN ATTENTE) ========== -->
    <h2 v-if="requestedTransactions.length !== 0" class="mt-3 dark--text font-weight-bold text-xs-left">
      <u>Requested Transactions</u>
    </h2>
    <div v-if="requestedTransactions.length !== 0" class="white elevation-6 pa-2" style="max-height: 50px; overflow: auto; border-radius: 7px">
      <div color="white" style="" v-for="item in requestedTransactions" :key="item.id">
        <v-card-text class="pa-1" style="font-size: 12px;">
          <v-layout row wrap v-if="!item.cancelled || item.withdrawal || (!item.withdrawal && !item.confirmed)">
            <v-flex xs8 text-xs-left>
              <div v-if="item.withdrawal">
                <span class="red--text font-weight-bold">Interrupt:</span><span> {{ formatAmount(item.amount) }} FCFA</span>
              </div>
              <div v-else>
                <span class="dark--text font-weight-bold">Deposit:</span><span> {{ formatAmount(item.amount) }} FCFA</span>
              </div>
              <div v-if="!item.confirmed && !item.cancelled" xs8 text-xs-left>
                <span class="font-weight-bold">Status:</span><span class="primary--text font-weight-bold"> Pending </span>
              </div>
              <div v-if="item.confirmed" xs8 text-xs-left>
                <span class="font-weight-bold">Status:</span><span class="success--text font-weight-bold"> Confirmed </span>
              </div>
            </v-flex>
            <v-flex xs4 text-xs-right>
              <span class="dark--text font-weight-bold">Date:</span><span> {{ item.date_time }}</span>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-divider></v-divider>
      </div>
    </div>

    <!-- ========== SECTION : TRANSACTIONS VALIDÉES ========== -->
    <h2 class="mt-3 dark--text font-weight-bold text-xs-left"><u>Validated Transactions</u></h2>
    <div class="white elevation-6 pa-2" style="max-height: 300px; overflow: auto; border-radius: 7px">
      <div color="white" style="" v-for="item in businessTransactions" :key="item.id">
        <v-card-text class="pa-1" style="font-size: 12px;">
          <v-layout row wrap>
            <v-flex xs6 text-xs-left>
              <div>
                <span class="dark--text font-weight-bold">Business Deposit:</span>
                <span> {{ formatAmount(item.amount) }} FCFA</span>
              </div>
            </v-flex>
            <v-flex xs6 text-xs-right>
              <span class="dark--text font-weight-bold">Date:</span>
              <span> {{ item.get_day_month_year_str }}</span>
            </v-flex>
            <v-flex xs8 text-xs-left>
              <span class="dark--text font-weight-bold"> By:</span>
              <span> {{ item.processed_by }} (Business Suite)</span>
            </v-flex>
            <v-flex xs4 text-xs-right>
              <span class="dark--text font-weight-bold">at</span>
              <span> {{ item.transaction_time }}</span>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-divider></v-divider>
      </div>
      <div color="white" style="" v-for="item in validatedTransactions" :key="item.id">
        <v-card-text class="pa-1" style="font-size: 12px;">
          <v-layout row wrap>
            <v-flex xs6 text-xs-left>
              <div v-if="item.withdrawal">
                <span class="dark--text font-weight-bold">Tranfered:</span><span> {{ formatAmount(item.amount) }} FCFA</span>
              </div>
              <div v-else>
                <span class="dark--text font-weight-bold">Deposit:</span><span> {{ formatAmount(item.amount) }} FCFA</span>
              </div>
            </v-flex>
            <v-flex xs6 text-xs-right>
              <span class="dark--text font-weight-bold">Date:</span><span> {{ item.get_day_month_year_str }}</span>
            </v-flex>
            <v-flex xs8 text-xs-left>
              <span class="dark--text font-weight-bold">By:</span><span> {{ item.top_up_admin_username }} - {{ item.top_up_admin_phone_number }}</span>
            </v-flex>
            <v-flex xs4 text-xs-right>
              <span class="dark--text font-weight-bold">at</span><span> {{ item.transaction_time }}</span>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-divider></v-divider>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'

export default {
  data() {
    return {
      loan: {},
      requestedTransactions: [],
      validatedTransactions: [],
      businessTransactions: [],
    }
  },

  computed: {
    showActionButton() {
      if (!this.loan.id) return false
      if (this.loan.is_legacy) {
        return this.loan.validated && !this.loan.cancelled
      } else {
        const activeStatuses = ['APPROVED', 'DISBURSED', 'REPAYING']
        return activeStatuses.includes(this.loan.status) && parseFloat(this.loan.remaining_amount || 0) > 0
      }
    },
    actionLabel() {
      return this.loan.is_legacy ? 'Save Now' : 'Repay Now'
    },
    actionIcon() {
      return this.loan.is_legacy ? 'fas fa-piggy-bank' : 'fas fa-money-bill-wave'
    },
    actionRoute() {
      return `/savings/loans/save/${this.loan.id}`
    },
    transactionTitle() {
      return this.loan.is_legacy ? 'Validated Transactions' : 'Repayment History'
    },
    statusColor() {
      const colors = {
        PENDING: 'orange',
        APPROVED: 'success',
        DISBURSED: 'primary',
        REPAYING: 'purple',
        COMPLETED: 'success',
        REJECTED: 'error',
        DEFAULTED: 'error'
      }
      return colors[this.loan.status] || 'grey'
    }
  },

  mounted() {
    this.getLoanDetail()
    this.getLoanTransactions()
    document.title = "PettyCash | Loan Details"
    this.$store.commit('setCloseURL', '/savings/loans/active')
  },

  methods: {
    async getLoanDetail() {
      this.$store.commit('setIsLoading', true)
      const loan_id = this.$route.params.loan_id
      try {
        const response = await axios.get(`/api/v1/savings/get/user/loan/detail/${loan_id}/`)
        this.loan = response.data
      } catch (error) {
        this.showError(error)
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },

    async getLoanTransactions() {
      const loan_id = this.$route.params.loan_id
      try {
        const response = await axios.get(`/api/v1/savings/get/user/loan/${loan_id}/transactions/`)
        this.requestedTransactions = response.data.requested || []
        this.validatedTransactions = response.data.validated || []
        this.businessTransactions = response.data.business || []
      } catch (error) {
        this.showError(error)
      }
    },

    formatAmount(amount) {
      if (!amount && amount !== 0) return '—'
      return new Intl.NumberFormat('fr-FR').format(amount)
    },

    showError(error) {
      let msg = 'An error occurred'
      if (error.response) {
        msg = error.response.data?.error || error.response.statusText
      }
      this.$store.commit('setSnackBarMessage', msg)
      this.$store.commit('setSnackBarColor', 'red darken-4')
      this.$store.commit('activateSnackBar', true)
    }
  }
}
</script>

<style scoped>
</style>